import { TextareaAutosize, Tooltip } from "@material-ui/core"
import Widgets from "components/Widgets"
import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import CurrencyInput from "react-currency-input-field"
import Loader from "react-loaders"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import {
  Card,
  CardBody,
  Col,
  Container,
  Label,
  Modal,
  Row,
  Input,
} from "reactstrap"
import api from "services/api.service"
import { userService } from "services/user.service"
import colors from "utils/colors"
import { currencyFormatter } from "utils/formatters"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { changeNavbarParams, loadPageItem } from "store/actions"
import { ObjFinAlert } from "services/scenario.service"
import { exportTableToExcel } from "utils/exportToExcel"
import CustomSelect from "components/Form/CustomSelect"
import SyncfusionGrid from "components/custom/Syncfusion/SyncfusionGrid"
import { DataManager, Query } from "@syncfusion/ej2-data"
import { DropDownList } from "@syncfusion/ej2-react-dropdowns"

const Decision = props => {
  const scenarioId = props.match.params.scenarioId
  const dispatch = useDispatch()
  const gridRef = useRef(null)
  const { pageItem } = useSelector(state => ({
    pageItem: state.pageItem.pageItem,
  }))
  const { optObjFin } = useSelector(state => ({
    optObjFin: state.optObjFin.optObjFin,
  }))
  const currentUser = userService.getLoggedInUser()
  const [isLoading, setIsLoading] = useState(false)
  const [alternatives, setAlternatives] = useState([])
  const [years, setYears] = useState([])
  const [invStatuses, setInvStatuses] = useState([])
  const [decisions, setDecisions] = useState([])
  const [widgets, setWidgets] = useState([])

  const [showExcludedFromPlanning, setShowExcludedFromPlanning] =
    useState(false)

  const widgetsRef = React.createRef()
  const [tableHeight, setTableHeight] = useState(`calc(100% - 100px)`)
  const { showWidgets } = useSelector(state => ({
    showWidgets: state.appSettings.showWidgets,
  }))

  useLayoutEffect(() => {
    if (showWidgets && widgets.length > 0 && widgetsRef.current) {
      const dif = widgetsRef.current.offsetHeight
      setTableHeight(`calc(100% - ${dif + 100}px)`)
    } else {
      setTableHeight("calc(100% - 100px)")
    }
  }, [showWidgets, widgets])

  useEffect(() => {
    dispatch(
      loadPageItem({
        userId: currentUser.userID,
        scenarioId: scenarioId,
        alternativeId: 0,
        viewName: "AnalysisDecision",
      })
    )
    dispatch(
      changeNavbarParams({
        userId: currentUser.userID,
        scenarioId: scenarioId,
        viewName: "AnalysisDecision",
      })
    )
    loadData()
  }, [])

  const loadData = async () => {
    setIsLoading(true)
    loadWidgets()
    let y = await api.getYears(currentUser.userID)
    setYears(y)
    let is = await api.getList(currentUser.userID, "InvestmentStatus")
    setInvStatuses(is)
    let d = await api.getList(currentUser.userID, "Decisions")
    setDecisions(d)
    await loadAlternatives(showExcludedFromPlanning)
    setIsLoading(false)
  }

  const loadWidgets = async () => {
    let w = await api.getWidgetsByView("MakeDecisions", scenarioId)
    setWidgets(w)
  }

  const loadAlternatives = async showExcluded => {
    let { obj, fin } = optObjFin
    let alts = await api.getAlternativesDecision(
      scenarioId,
      obj == null ? 0 : obj.objModelID,
      fin == null ? 0 : fin.finModelID,
      showExcluded
    )

    let enrichedAlts = enrichWithRunningCostAndBenefitFields(alts)

    setAlternatives(enrichedAlts)
  }

  const enrichWithRunningCostAndBenefitFields = alts => {
    let copy = [...alts]
    for (let i = 0; i < copy.length; i++) {
      if (i == 0) {
        copy[i].runningCost = copy[i].cost
        copy[i].runningBenefit = copy[i].saaScore3
      } else {
        let prev = copy[i - 1]
        copy[i].runningCost = prev.runningCost + copy[i].cost
        copy[i].runningBenefit = prev.runningBenefit + copy[i].saaScore3
      }
    }
    return copy
  }

  const exportTable = () => {
    const colsToExport = [
      { property: "alternativeNum", heading: "ID" },
      { property: "alternative", heading: "Project" },
      {
        property: "saaScore3",
        heading: "Benefit Score",
        formatter: row => (row.saaScore3 || 0) + "%",
      },
      { property: "runningBenefit", heading: "Running Benefit" },
      { property: "saaRating", heading: "Group Rating" },
      { property: "cost", heading: "Cost" },
      { property: "runningCost", heading: "Running Cost" },
      { property: "fundedAmount", heading: "Funded" },
      { property: "remainingAmount", heading: "Remaining" },
      { property: "dependencies", heading: "Dependencies" },
      { property: "fySubmitted", heading: "FY Submitted" },
      { property: "fyPlanned", heading: "FY Planned" },
      { property: "investmentStatus", heading: "Investment Status" },
      { property: "decision", heading: "Decision" },
      { property: "decisionNotes", heading: "Notes" },
      { property: "highResource", heading: "Resource Risk" },
    ]
    exportTableToExcel(alternatives, colsToExport, "DecisionAnalysis.csv")
  }

  let dropdownObj

  const columns = [
    {
      field: "alternativeNum",
      headerText: "ID",
      width: 80,
      freeze: "Left",
      allowEditing: false,
    },
    {
      field: "alternative",
      headerText: "Project",
      width: 300,
      freeze: "Left",
      clipMode: "EllipsisWithTooltip",
      allowEditing: false,
    },
    {
      field: "version",
      headerText: "Version",
      width: 100,
      freeze: "Left",
      allowEditing: false,
      edit: {
        create: () => {
          const container = document.createElement("div")
          container.style.lineHeight = "20px"
          container.className = "e-cell-readonly e-disabled"
          return container
        },
        write: args => {
          args.element.innerHTML = args.rowData.version || ""
        },
        read: () => null,
      },
      filter: {
        type: "Excel",
        itemTemplate: data => {
          return (
            <div
              style={{ lineHeight: "20px" }}
              dangerouslySetInnerHTML={{
                __html: data.version,
              }}
            ></div>
          )
        },
      },
      template: data => (
        <div
          style={{ lineHeight: "20px" }}
          dangerouslySetInnerHTML={{
            __html: data.version,
          }}
        ></div>
      ),
    },
    {
      field: "saaScore",
      headerText: "Benefit Score",
      width: 230,
      textAlign: "Right",
      allowEditing: false,
      edit: {
        create: () => {
          const container = document.createElement("div")
          container.style.lineHeight = "20px"
          container.className = "e-cell-readonly e-disabled"
          return container
        },
        write: args => {
          args.element.innerHTML = args.rowData.saaScore || ""
        },
        read: () => null,
      },
      filter: {
        type: "Excel",
        itemTemplate: data => {
          return (
            <div
              style={{ lineHeight: "20px" }}
              dangerouslySetInnerHTML={{
                __html: data.saaScore,
              }}
            ></div>
          )
        },
      },
      template: data => (
        <div
          style={{ lineHeight: "20px" }}
          dangerouslySetInnerHTML={{
            __html: data.saaScore,
          }}
        ></div>
      ),
    },
    {
      field: "runningBenefit",
      headerText: "Running Benefit",
      width: 120,
      allowEditing: false,
      filter: {
        type: "Excel",
        itemTemplate: data => parseFloat(data.runningBenefit).toFixed(2),
      },
      textAlign: "Right",
      template: data => parseFloat(data.runningBenefit).toFixed(2),
    },
    {
      field: "saaRating",
      headerText: "Group Rating",
      width: 150,
      allowEditing: false,
      edit: {
        create: () => {
          const container = document.createElement("div")
          container.style.lineHeight = "20px"
          container.className = "e-cell-readonly e-disabled"
          return container
        },
        write: args => {
          args.element.innerHTML = args.rowData.saaRating || ""
        },
        read: () => null,
      },
      filter: {
        type: "Excel",
        itemTemplate: data => {
          return (
            <div
              style={{ lineHeight: "20px" }}
              dangerouslySetInnerHTML={{
                __html: data.saaRating,
              }}
            ></div>
          )
        },
      },
      template: data => (
        <div
          style={{ lineHeight: "20px" }}
          dangerouslySetInnerHTML={{
            __html: data.saaRating,
          }}
        ></div>
      ),
    },
    {
      field: "cost",
      headerText: "Cost",
      width: 120,
      allowEditing: false,
      filter: {
        type: "Excel",
        itemTemplate: data => currencyFormatter.format(data.cost),
      },
      textAlign: "Right",
      template: data => currencyFormatter.format(data.cost),
    },
    {
      field: "costBenefit",
      headerText: "Benefit / Cost Ratio",
      width: 250,
      textAlign: "Right",
      allowEditing: false,
      filter: {
        type: "Excel",
        itemTemplate: data => parseFloat(data.costBenefit).toFixed(2),
      },
      template: data => parseFloat(data.costBenefit).toFixed(2),
    },
    {
      field: "runningCost",
      headerText: "Running Cost",
      allowEditing: false,
      width: 180,
      filter: {
        type: "Excel",
        itemTemplate: data => currencyFormatter.format(data.runningCost),
      },
      textAlign: "Right",
      template: data => currencyFormatter.format(data.runningCost),
    },
    {
      field: "fundedAmount",
      headerText: "Funded",
      width: 120,
      filter: {
        type: "Excel",
        itemTemplate: data => currencyFormatter.format(data.fundedAmount ?? 0),
      },
      template: data => currencyFormatter.format(data.fundedAmount ?? 0),
    },
    {
      field: "remainingAmount",
      headerText: "Remaining Amount",
      width: 180,
      allowEditing: false,
      filter: {
        type: "Excel",
        itemTemplate: data => currencyFormatter.format(data.remainingAmount),
      },
      textAlign: "Right",
      template: data => currencyFormatter.format(data.remainingAmount),
    },
    {
      field: "dependencies",
      headerText: "Dependencies",
      width: 120,
      allowFiltering: false,
      allowEditing: false,
    },
    {
      field: "fySubmitted",
      headerText: "FY Submitted",
      width: 150,
      editType: "dropdownedit",
      edit: {
        params: {
          actionComplete: () => false,
          dataSource: new DataManager(
            years.map((y, idx) => ({
              id: idx,
              value: parseInt(y.listItemName),
              text: y.listItemName,
            }))
          ),
          fields: { text: "text", value: "value" },
          query: new Query(),
        },
      },
    },
    {
      field: "fyPlanned",
      headerText: "FY Planned",
      width: 150,
      editType: "dropdownedit",
      edit: {
        params: {
          actionComplete: () => false,
          dataSource: new DataManager(
            years.map((y, idx) => ({
              id: idx,
              value: parseInt(y.listItemName),
              text: y.listItemName,
            }))
          ),
          fields: { text: "text", value: "value" },
          query: new Query(),
        },
      },
    },
    {
      field: "investmentStatusID",
      headerText: "Investment Status",
      width: 180,

      editType: "dropdownedit",
      edit: {
        params: {
          actionComplete: () => false,
          dataSource: new DataManager(
            invStatuses.map((y, idx) => ({
              id: idx,
              value: parseInt(y.listItemID),
              text: y.listItemName,
            }))
          ),
          fields: { text: "text", value: "value" },
          query: new Query(),
        },
      },
      filter: {
        type: "Excel",
        itemTemplate: data =>
          invStatuses.find(x => x.listItemID == data.investmentStatusID)
            ?.listItemName || "",
      },

      template: data => {
        return (
          invStatuses.find(x => x.listItemID == data.investmentStatusID)
            ?.listItemName || ""
        )
      },
    },
    {
      field: "decisionID",
      headerText: "Decision",
      width: 180,
      edit: {
        create: () => {
          const select = document.createElement("select")
          select.style.width = "100%"
          select.style.padding = "4px"
          select.style.height = "32px"
          select.style.border = "1px solid #ccc"

          // Create options
          decisions.forEach(status => {
            const option = document.createElement("option")
            option.value = status.listItemID
            option.textContent = status.listItemName
            option.style.backgroundColor = status.backColor
            option.style.color = status.foreColor
            select.appendChild(option)
          })

          return select
        },
        write: args => {
          const select = args.element
          
          select.value =  args.rowData.decisionID?.toString() || ""
        },
        read: args => {
          const val =
            args.selectedOptions?.length > 0
              ? args.selectedOptions[0].value
              : args.rowData.decisionID?.toString()
          return parseInt(val, 10) 
        },
        destroy: () => {},
      },
      // editType: "dropdownedit",
      // edit: {
      //   params: {
      //     actionComplete: () => false,
      //     dataSource: new DataManager(
      //       decisions.map((y, idx) => ({
      //         id: idx,
      //         value: parseInt(y.listItemID),
      //         text: y.listItemName,
      //       }))
      //     ),
      //     fields: { text: "text", value: "value" },
      //     query: new Query(),
      //   },
      // },
      filter: {
        type: "Excel",
        itemTemplate: data =>
          decisions.find(x => x.listItemID == data.decisionID)?.listItemName ||
          "",
      },
      template: data => {
        return (
          <div
            style={{
              color:
                decisions.find(x => x.listItemID == data.decisionID)
                  ?.foreColor || "black",
              backgroundColor:
                decisions.find(x => x.listItemID == data.decisionID)
                  ?.backColor || "white",
              lineHeight: "25px",
            }}
          >
            {decisions.find(x => x.listItemID == data.decisionID)
              ?.listItemName || ""}
          </div>
        )
      },
    },
    {
      field: "decisionNotes",
      headerText: "Notes",
      width: 300,
      allowFiltering: false,
      clipMode: "EllipsisWithTooltip",
    },
    {
      field: "highResource",
      headerText: "Resource Risk",
      width: 120,
      textAlign: "Center",
      allowFiltering: false,
      editType: "booleanedit",
      displayAsCheckBox: true,
    },
  ]

  const onActionComplete = async args => {
    if (args.requestType === "sorting" || args.requestType === "filtering") {
      updateRunningCost()
    } else if (args.requestType === "save") {
      await api.updateAlternative(currentUser.userID, args.data)
    }
  }

  const updateRunningCost = () => {
    const grid = gridRef.current
    if (!grid) return

    const visibleData = grid.getCurrentViewRecords() // only visible rows

    setAlternatives(enrichWithRunningCostAndBenefitFields(visibleData))
  }

  return (
    <React.Fragment>
      <div className="page-content" style={{ height: "100vh" }}>
        <Container fluid style={{ height: "100%" }}>
          <Breadcrumbs
            title={pageItem !== undefined ? pageItem.pageSubTitle : ""}
          />
          <Card style={{ height: "100%" }}>
            <CardBody style={{ height: "100%" }}>
              {isLoading && (
                <Loader
                  type="line-scale-pulse-out"
                  color={colors.primary}
                  style={{ textAlign: "center" }}
                />
              )}
              <div ref={widgetsRef}>
                <Widgets widgets={widgets} marginBottom="20px" />
              </div>
              <ObjFinAlert optObjFin={optObjFin} />
              {!isLoading && (
                <div style={{ height: "100%" }}>
                  <Row>
                    <Col sm="8">
                      <div>
                        <Input
                          id="showExcludedFromPlanning"
                          type="checkbox"
                          className="form-check-input"
                          checked={showExcludedFromPlanning}
                          onClick={() => {
                            let newVal = !showExcludedFromPlanning
                            setShowExcludedFromPlanning(newVal)
                            loadAlternatives(newVal)
                          }}
                        />
                        <Label
                          for="showExcludedFromPlanning"
                          className="form-check-label"
                          style={{ marginLeft: "10px" }}
                        >
                          Show Excluded from Planning Cycle
                        </Label>
                      </div>
                    </Col>
                    <Col sm="4">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "flex-end",
                        }}
                      >
                        <h6>
                          <i
                            className="fas fa-file-excel"
                            style={{ cursor: "pointer" }}
                            onClick={exportTable}
                          ></i>
                          &nbsp;Export
                        </h6>
                      </div>
                    </Col>
                  </Row>
                  <div style={{ overflowY: "auto", height: tableHeight }}>
                    <SyncfusionGrid
                      gridRef={gridRef}
                      columns={columns}
                      data={alternatives}
                      allowPaging={false}
                      actionComplete={onActionComplete}
                      editSettings={{
                        allowEditing: true,
                        allowAdding: false,
                        allowDeleting: false,
                        mode: "Normal",
                      }}
                    />
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Decision
